/**
 * Renders the effective and explicit access attached to a Vault resource.
 *
 * Workspace membership is inherited, whole-Workspace-Vault access is binary,
 * and item grants may carry view or edit roles. Keeping those presentations in
 * one component makes that product distinction visible without moving policy
 * enforcement out of the backend sharing service.
 */
import { Crown, Globe, ShieldCheck, UserRound, X } from 'lucide-react'

import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import type { SharingResponse, VaultGrant, VaultRole } from '../types'
import { VaultAvatar } from './ShareCandidateGroup'

export type ShareRole = Extract<VaultRole, 'viewer' | 'editor'>

export const ROLE_LABELS: Record<ShareRole, string> = {
  viewer: 'Can view',
  editor: 'Can edit',
}

interface VaultGrantListProps {
  sharing?: SharingResponse
  userGrants: VaultGrant[]
  organizationGrant?: VaultGrant
  canShare: boolean
  readOnly: boolean
  workspaceRoot: boolean
  onSetUserRole: (userId: string, role: ShareRole) => void
  onRemoveUser: (userId: string) => void
  onSetOrganizationRole: (role: ShareRole | 'none') => void
}

export function VaultGrantList({
  sharing,
  userGrants,
  organizationGrant,
  canShare,
  readOnly,
  workspaceRoot,
  onSetUserRole,
  onRemoveUser,
  onSetOrganizationRole,
}: VaultGrantListProps) {
  return (
    <>
      {!!sharing?.workspaceAccess.length && (
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Workspace team
          </p>
          {sharing.workspaceAccess.map(access => (
            <div
              key={access.userId}
              className="-mx-2 flex items-center gap-3 rounded-lg px-2 py-1.5"
            >
              <VaultAvatar userId={access.userId} />
              <span className="flex-1 truncate text-sm font-medium">
                {access.userId}
              </span>
              <Badge variant="secondary" className="gap-1 capitalize">
                {access.workspaceRole === 'admin'
                  ? <ShieldCheck className="h-3 w-3" />
                  : <UserRound className="h-3 w-3" />}
                {/* This chip shows the inherited workspace role — the reason
                    this person has Vault access — so it is qualified as such
                    rather than reading as a separate bare "admin" level. */}
                {access.workspaceRole === 'admin'
                  ? 'workspace admin'
                  : 'workspace member'}
              </Badge>
            </div>
          ))}
          <p className="pt-1 text-xs text-muted-foreground">
            Workspace admins own Vault access. Workspace members can add and
            organize Vault content.
          </p>
        </div>
      )}

      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {workspaceRoot ? 'Additional Vault access' : 'People with access'}
        </p>
        {sharing?.ownerUserId && (
          <div className="-mx-2 flex items-center gap-3 rounded-lg px-2 py-1.5">
            <VaultAvatar userId={sharing.ownerUserId} />
            <span className="flex-1 truncate text-sm font-medium">
              {sharing.ownerUserId}
            </span>
            <Badge variant="secondary" className="gap-1">
              <Crown className="h-3 w-3 text-amber-500" /> Owner
            </Badge>
          </div>
        )}
        {userGrants.map(grant => {
          const userId = grant.principalId ?? '?'
          return (
            <div
              key={userId}
              className="-mx-2 flex items-center gap-3 rounded-lg px-2 py-1.5"
            >
              <VaultAvatar userId={userId} />
              <span className="flex-1 truncate text-sm">{userId}</span>
              {workspaceRoot ? (
                <>
                  <Badge variant="outline">Has access</Badge>
                  {canShare && grant.principalId && (
                    <RemoveAccessButton
                      userId={grant.principalId}
                      onRemove={onRemoveUser}
                    />
                  )}
                </>
              ) : canShare && grant.principalId ? (
                <>
                  <RoleSelect
                    role={grant.role as ShareRole}
                    readOnly={readOnly}
                    onChange={role => onSetUserRole(grant.principalId!, role)}
                  />
                  <RemoveAccessButton
                    userId={grant.principalId}
                    onRemove={onRemoveUser}
                  />
                </>
              ) : (
                <Badge variant="outline">
                  {ROLE_LABELS[grant.role as ShareRole] ?? grant.role}
                </Badge>
              )}
            </div>
          )
        })}
        <div className="-mx-2 mt-1 flex items-center gap-3 border-t px-2 py-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Globe className="h-4 w-4" />
          </span>
          <span className="flex-1 truncate text-sm">Everyone at UMD</span>
          {workspaceRoot ? (
            canShare ? (
              <Select
                value={organizationGrant ? 'viewer' : 'none'}
                onValueChange={value => onSetOrganizationRole(value as 'viewer' | 'none')}
              >
                <SelectTrigger className="h-8 w-32 shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No access</SelectItem>
                  <SelectItem value="viewer">Has access</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Badge variant="outline">
                {organizationGrant ? 'Has access' : 'No access'}
              </Badge>
            )
          ) : canShare ? (
            <Select
              value={organizationGrant?.role ?? 'none'}
              onValueChange={value => onSetOrganizationRole(value as ShareRole | 'none')}
            >
              <SelectTrigger className="h-8 w-32 shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No access</SelectItem>
                <SelectItem value="viewer">{ROLE_LABELS.viewer}</SelectItem>
                {!readOnly && (
                  <SelectItem value="editor">{ROLE_LABELS.editor}</SelectItem>
                )}
              </SelectContent>
            </Select>
          ) : (
            <Badge variant="outline">
              {organizationGrant
                ? ROLE_LABELS[organizationGrant.role as ShareRole] ?? organizationGrant.role
                : 'No access'}
            </Badge>
          )}
        </div>
        {!!sharing?.inheritedGrants.length && (
          <div className="space-y-1 border-t pt-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Inherited from parent folders
            </p>
            {sharing.inheritedGrants.map((grant, index) => (
              <div
                key={`${grant.resourceId}-${grant.principalId}-${index}`}
                className="flex items-center gap-3 py-1.5 text-sm"
              >
                <span className="flex-1 truncate">
                  {grant.orgPrincipal ? 'Everyone at UMD' : grant.principalId}
                </span>
                <Badge variant="outline">
                  {ROLE_LABELS[grant.role as ShareRole] ?? grant.role}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

function RoleSelect({
  role,
  readOnly,
  onChange,
}: {
  role: ShareRole
  readOnly: boolean
  onChange: (role: ShareRole) => void
}) {
  return (
    <Select value={role} onValueChange={value => onChange(value as ShareRole)}>
      <SelectTrigger className="h-8 w-32 shrink-0">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {!readOnly && <SelectItem value="editor">{ROLE_LABELS.editor}</SelectItem>}
        <SelectItem value="viewer">{ROLE_LABELS.viewer}</SelectItem>
      </SelectContent>
    </Select>
  )
}

function RemoveAccessButton({
  userId,
  onRemove,
}: {
  userId: string
  onRemove: (userId: string) => void
}) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8 text-muted-foreground hover:text-destructive"
      aria-label={`Remove access for ${userId}`}
      onClick={() => onRemove(userId)}
    >
      <X className="h-4 w-4" />
    </Button>
  )
}
